import { useState, useEffect, useCallback } from 'react';
import { log } from '@/lib/logger';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 14;
const IOS_HELP_DELAY_MS = 4000;

function wasRecentlyDismissed() {
  try {
    const iso = sessionStorage.getItem(DISMISS_KEY);
    if (!iso) return false;
    const dismissedMs = new Date(iso).getTime();
    if (Number.isNaN(dismissedMs)) return false;
    return Date.now() - dismissedMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (e) {
    log.debug('PWA prompt: dismissed-check failed', e);
    return false;
  }
}

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIosSafari() {
  const ua = window.navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

/**
 * Hook that manages PWA install prompt state.
 * - Chrome / Edge / Android → native `beforeinstallprompt`.
 * - iOS Safari → time-delayed help card (Safari has no install event).
 */
export function usePwaInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const hideNow = useCallback(() => {
    setVisible(false);
    setShowIosHelp(false);
  }, []);

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, new Date().toISOString());
    } catch (e) {
      log.debug('PWA prompt: dismiss persist failed', e);
    }
    hideNow();
  }, [hideNow]);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVisible(true);
    };
    const onInstalled = () => hideNow();

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    let iosTimer;
    if (isIosSafari()) {
      iosTimer = setTimeout(() => {
        setVisible((prev) => prev || true);
      }, IOS_HELP_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [hideNow]);

  const install = useCallback(async () => {
    if (!deferredEvent) {
      if (isIosSafari()) setShowIosHelp(true);
      return;
    }
    try {
      deferredEvent.prompt();
      const choice = await deferredEvent.userChoice;
      if (choice?.outcome === 'accepted') hideNow();
      else dismiss();
    } catch (e) {
      log.debug('PWA prompt: install() failed', e);
      dismiss();
    } finally {
      setDeferredEvent(null);
    }
  }, [deferredEvent, dismiss, hideNow]);

  return { visible, showIosHelp, install, dismiss, deferredEvent };
}
